import type { JSX, ElementType } from 'react';

import { ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from '@mui/icons-material/Place';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Routes } from '@/constants/routes';

export type OrdersView = 'list' | 'calendar' | 'addresses';

// Sub-route per view — the switch owns the mapping so no call site hardcodes a
// path (CHR-06 §1 added Adresar as the third sibling).
const VIEW_ROUTE: Record<OrdersView, string> = {
  list: Routes.ORDERS,
  calendar: Routes.ORDERS_CALENDAR,
  addresses: Routes.ORDERS_ADDRESSES,
};

// One row per view — iconography mirrors the content (rows / calendar / pin).
const VIEW_ITEMS: readonly { view: OrdersView; Icon: ElementType; labelKey: string }[] = [
  { view: 'list', Icon: ViewListIcon, labelKey: 'calendar.viewList' },
  { view: 'calendar', Icon: CalendarMonthIcon, labelKey: 'calendar.viewCalendar' },
  { view: 'addresses', Icon: PlaceIcon, labelKey: 'calendar.viewAddresses' },
];

export interface OrdersViewToggleProps {
  // Which view the host page represents — drives the selected button.
  value: OrdersView;
  // Opt-in to the full-bleed compact group (< `md`). Only Adresář asks for it:
  // the Seznam and Kalendář toolbars keep the toggle content-width on the same
  // row as Export / Nová jízda, and stretching it there reflowed those rows.
  stretchOnCompact?: boolean;
}

// Seznam | Kalendář | Adresář switch (CHR-03 §6 / ADR-0051, third item added by
// CHR-06 §1). Rendered in ALL THREE toolbars so the views read as one system.
// Navigates between `/orders`, `/orders/calendar` and `/orders/addresses`; the
// host page's `value` prop marks the active button. Default (overview) = Seznam.
//
// Below `md` (`prototyp.html#s-mobile`) only the ACTIVE item keeps its label —
// three Czech labels do not fit a 375px row next to the search field. The
// inactive buttons stay screen-reader-reachable through `aria-label`. The
// full-bleed equal-width stretch is opt-in (`stretchOnCompact`) because the
// Seznam / Kalendář toolbars pair the toggle with buttons on the same row.
export const OrdersViewToggle = ({
  value,
  stretchOnCompact = false,
}: OrdersViewToggleProps): JSX.Element => {
  const { t } = useTranslation('orders');
  const theme = useTheme();
  const navigate = useNavigate();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const isStretched = isCompact && stretchOnCompact;

  const handleChange = (
    _e: React.MouseEvent<HTMLElement>,
    next: OrdersView | null,
  ): void => {
    // Ignore the de-select click (MUI fires `null` when the active button is
    // re-pressed) — the views are mutually exclusive, one is always selected.
    if (next === null || next === value) return;
    navigate(VIEW_ROUTE[next]);
  };

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={handleChange}
      aria-label={t('calendar.viewToggleAria')}
      data-testid="orders-view-toggle"
      // Driven by the JS media query rather than an `sx` breakpoint object so
      // the stretched layout and the icon-only labels switch on the SAME
      // boundary — a CSS-only width with a JS-decided label would desync.
      sx={{
        // Full-bleed on phones/tablets only where the host asks for it.
        width: isStretched ? '100%' : 'auto',
        '& .MuiToggleButton-root': {
          // Tap target ≥44px (touch / tablet) — MUI's small toggle is ~30px.
          minHeight: 44,
          px: isCompact ? 1 : 1.75,
          // Equal shares of the stretched compact group.
          flex: isStretched ? 1 : 'none',
        },
      }}
    >
      {VIEW_ITEMS.map(({ view, Icon, labelKey }) => {
        const label = t(labelKey);
        const showLabel = !isCompact || view === value;
        return (
          <ToggleButton
            key={view}
            value={view}
            aria-label={label}
            data-testid={`orders-view-toggle-${view}`}
          >
            <Icon sx={{ fontSize: 18, mr: showLabel ? 0.75 : 0 }} />
            {showLabel ? label : null}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
};
